import { useCallback, useState } from 'react';
import { useCurrentUserDetails } from '../../entities/user/model';
import { useAuth0 } from '@auth0/auth0-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { QUERY_KEY } from '../../entities/invoice/model/constants';
import _ from 'lodash';
import axios from 'axios';

async function fetchInvoicesRequest(
  token: string,
  organizationId: string,
  queryOptions: QueryOptions
) {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_SERVER_URL}/api/public/invoices/list`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          filter: _.omitBy(
            {
              generalSearchString: queryOptions.generalSearchString,
              fromCreatedAtDate: queryOptions.fromCreatedAtDate,
              toCreatedAtDate: queryOptions.toCreatedAtDate,
              paymentStatus: queryOptions.paymentStatus,
            },
            (v) => _.isUndefined(v) || v === ''
          ),
          pageNumber: queryOptions.pageNumber,
          pageLimit: queryOptions.pageLimit,
          organizationId,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.log(error);
    return [];
  }
}

type QueryOptions = {
  pageNumber: number;
  pageLimit: number;
  generalSearchString?: string;
  fromCreatedAtDate?: string;
  toCreatedAtDate?: string;
  paymentStatus?: string;
};

export const INIT_INVOICES_PAGINATION = { pageIndex: 0, pageSize: 10 };

export const useOrganizationInvoices = (
  initQueryOptions: QueryOptions = {
    pageNumber: INIT_INVOICES_PAGINATION.pageIndex + 1,
    pageLimit: INIT_INVOICES_PAGINATION.pageSize,
  }
) => {
  const [queryOptions, setQueryOptions] =
    useState<QueryOptions>(initQueryOptions);
  const { getCurrentOrganizationId, hasLoggedInUser } = useCurrentUserDetails();
  const { getAccessTokenSilently } = useAuth0();
  const { data, isPending, isFetching } = useQuery({
    queryKey: [
      QUERY_KEY,
      queryOptions.pageNumber,
      queryOptions.generalSearchString,
      queryOptions.fromCreatedAtDate,
      queryOptions.toCreatedAtDate,
      queryOptions.paymentStatus,
    ],
    queryFn: () =>
      getAccessTokenSilently().then((token) =>
        fetchInvoicesRequest(token, getCurrentOrganizationId(), queryOptions)
      ),
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60,
    enabled: hasLoggedInUser,
  });

  const _updateQueryOptions = useCallback(
    ({
      generalSearchString,
      pageNumber,
      pageLimit,
      paymentStatus,
      toCreatedAtDate,
      fromCreatedAtDate,
    }: Partial<QueryOptions>) => {
      const update: Partial<QueryOptions> = {};

      if (pageNumber !== undefined) {
        update.pageNumber = pageNumber;
      }

      if (pageLimit !== undefined) {
        update.pageLimit = pageLimit;
      }

      if (generalSearchString !== undefined) {
        update.generalSearchString = generalSearchString;
      }

      if (fromCreatedAtDate !== undefined) {
        update.fromCreatedAtDate = fromCreatedAtDate;
      }

      if (toCreatedAtDate !== undefined) {
        update.toCreatedAtDate = toCreatedAtDate;
      }

      if (!_.isUndefined(paymentStatus)) {
        update.paymentStatus = paymentStatus;
      }

      setQueryOptions({
        ...queryOptions,
        ...update,
      });
    },
    [setQueryOptions, queryOptions]
  );

  const _hasFilterOptions = useCallback(() => {
    return !(
      (_.isUndefined(queryOptions.generalSearchString) ||
        queryOptions.generalSearchString === '') &&
      (_.isUndefined(queryOptions.fromCreatedAtDate) ||
        queryOptions.fromCreatedAtDate === '') &&
      (_.isUndefined(queryOptions.toCreatedAtDate) ||
        queryOptions.toCreatedAtDate === '') &&
      (_.isUndefined(queryOptions.paymentStatus) ||
        queryOptions.paymentStatus === '')
    );
  }, [queryOptions]);

  return {
    invoices: data?.result || [],
    invoicesTotal: data?.total || -1,
    isLoading: isPending,
    isFetching,
    queryOptions,
    hasFilterOptions: _hasFilterOptions,
    updateQueryOptions: _updateQueryOptions,
  };
};
